import { produce } from "immer"
import { undoableReducer } from "../undoable/undoable.reducer"
import type { IUndoableState, Reducer } from "../undoable/undoable.types"
import {
  PROJECT_ADD_ATTACHMENT,
  PROJECT_ADD_DRAWING,
  PROJECT_ADD_POINT_TO_POLYLINE,
  PROJECT_CLEAR,
  PROJECT_CLEAR_HISTORY,
  PROJECT_CLOSE_DRAWING_POPUP,
  PROJECT_EDIT_DRAWING_COLOR,
  PROJECT_EDIT_DRAWING_POPUP,
  PROJECT_EDIT_DRAWING_TEXT,
  PROJECT_EDIT_DRAWING_WEIGHT,
  PROJECT_LOAD,
  PROJECT_MOVE_DRAWING,
  PROJECT_REDO,
  PROJECT_REMOVE_ATTACHMENT,
  PROJECT_REMOVE_DRAWING,
  PROJECT_SYNC_POPUP_STATES,
  PROJECT_UNDO,
  PROJECT_UPDATE_ATTACHMENT,
  PROJECT_UPDATE_ATTACHMENTS
} from "./projects.actions"
import type { Attachment, IMapDrawing, IProjectState, Project, ProjectActions } from "./projects.types"
export const projectsInitialState: IProjectState = {
  isDirty: false
}

export const undoableProjectReducer: Reducer<IUndoableState<IProjectState>, ProjectActions> = undoableReducer<
  IProjectState,
  ProjectActions
>(projectReducer, {
  undoAction: PROJECT_UNDO,
  redoAction: PROJECT_REDO,
  clearAction: PROJECT_CLEAR_HISTORY,
  undoableActions: [
    PROJECT_ADD_DRAWING,
    PROJECT_REMOVE_DRAWING,
    PROJECT_MOVE_DRAWING,
    PROJECT_EDIT_DRAWING_WEIGHT,
    PROJECT_EDIT_DRAWING_COLOR,
    PROJECT_EDIT_DRAWING_TEXT
  ],
  compoundUndoableActions: [PROJECT_MOVE_DRAWING, PROJECT_EDIT_DRAWING_WEIGHT],
  ignoreActions: [PROJECT_SYNC_POPUP_STATES]
})

export function projectReducer(currentState: IProjectState | undefined, action: ProjectActions): IProjectState {
  const state = currentState ?? projectsInitialState
  switch (action.type) {
    case PROJECT_CLEAR: {
      return clearProject(state)
    }
    case PROJECT_ADD_DRAWING: {
      return addDrawing(state, action.payload as IMapDrawing)
    }
    case PROJECT_REMOVE_DRAWING: {
      return removeDrawing(state, action.payload as IMapDrawing)
    }
    case PROJECT_LOAD: {
      return setProject(state, action.payload as Project)
    }
    case PROJECT_EDIT_DRAWING_COLOR: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_EDIT_DRAWING_WEIGHT: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_EDIT_DRAWING_POPUP: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_CLOSE_DRAWING_POPUP: {
      return closeDrawingPopup(state)
    }
    case PROJECT_MOVE_DRAWING: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_ADD_POINT_TO_POLYLINE: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_EDIT_DRAWING_TEXT: {
      const drawing: IMapDrawing = action.payload as IMapDrawing
      return editDrawing(state, drawing)
    }
    case PROJECT_ADD_ATTACHMENT: {
      return addAttachment(state, action.payload as Attachment)
    }
    case PROJECT_REMOVE_ATTACHMENT:
      return removeAttachment(state, action.payload as Attachment)
    case PROJECT_UPDATE_ATTACHMENT: {
      return updateAttachment(state, action.payload as Attachment)
    }
    case PROJECT_UPDATE_ATTACHMENTS: {
      return updateAttachments(state, action.payload as Attachment[])
    }
    case PROJECT_SYNC_POPUP_STATES: {
      return setDrawings(state, action.payload as IMapDrawing[])
    }
    default:
      return state
  }
}

function clearProject(currentState: IProjectState): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    draftState.project = undefined
    draftState.isDirty = false
  })
}

function addDrawing(currentState: IProjectState, drawing: IMapDrawing): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    draftState.project?.drawings.push(drawing)
    draftState.isDirty = true
  })
}

function removeDrawing(currentState: IProjectState, drawing: IMapDrawing): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (draftState.project) {
      draftState.project.drawings = draftState.project.drawings.filter((filterDrawing: IMapDrawing) => {
        return filterDrawing.id !== drawing.id
      })
      draftState.isDirty = true
    }
  })
}

function editDrawing(currentState: IProjectState, editedDrawing: IMapDrawing): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.drawings = draftState.project.drawings.map((drawing: IMapDrawing) => {
      if (drawing.id === editedDrawing.id) {
        return editedDrawing
      }
      return drawing
    })
    draftState.isDirty = true
  })
}

function closeDrawingPopup(currentState: IProjectState) {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.drawings = draftState.project.drawings.map((drawing: IMapDrawing) => {
      if (drawing.isPopupOpen) {
        return { ...drawing, isPopupOpen: false }
      }
      return drawing
    })
  })
}

function setProject(currentState: IProjectState, project: Project): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    draftState.project = project
    draftState.isDirty = false
  })
}

function setDrawings(currentState: IProjectState, drawings: IMapDrawing[]): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return
    draftState.project.drawings = drawings
  })
}

function addAttachment(currentState: IProjectState, attachment: Attachment): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.attachments.push(attachment)
    draftState.isDirty = true
  })
}

function removeAttachment(currentState: IProjectState, attachment: Attachment): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.attachments = draftState.project.attachments
      .filter((a: Attachment) => a.order !== attachment.order)
      .map((a: Attachment, index: number) => {
        a.order = index + 1
        return a
      })
    draftState.isDirty = true
  })
}

function updateAttachment(currentState: IProjectState, attachment: Attachment): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.attachments[attachment.order - 1] = attachment
    draftState.isDirty = true
  })
}

function updateAttachments(currentState: IProjectState, attachments: Attachment[]): IProjectState {
  return produce(currentState, (draftState: IProjectState) => {
    if (!draftState.project) return

    draftState.project.attachments = attachments
    draftState.isDirty = true
  })
}
